import { useState, useEffect } from 'react';
import { 
  AlertTriangle, 
  Plus, 
  Edit2, 
  Trash2, 
  HelpCircle,
  CheckCircle2,
  ArrowRight,
  Save,
  X,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Switch } from '../components/ui/switch';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { 
  getTroubleshootingFlows, 
  createTroubleshootingFlow, 
  updateTroubleshootingFlow, 
  deleteTroubleshootingFlow 
} from '../lib/api';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const defaultStep = () => ({
  id: `step-${Date.now()}`,
  question: '',
  options: [],
  solution: ''
});

export const TroubleshootingPage = () => {
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [flowDialog, setFlowDialog] = useState({ open: false, data: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [expandedFlows, setExpandedFlows] = useState({});
  
  // Form state
  const [flowForm, setFlowForm] = useState({
    name: '',
    description: '',
    trigger_keywords: [],
    steps: [defaultStep()],
    is_active: true
  });
  const [keywordInput, setKeywordInput] = useState('');

  useEffect(() => {
    loadFlows();
  }, []);

  const loadFlows = async () => {
    try {
      setLoading(true);
      const res = await getTroubleshootingFlows();
      setFlows(res.data);
    } catch (error) {
      // Production: error logged silently
      toast.error('Failed to load troubleshooting flows');
    } finally {
      setLoading(false);
    }
  };

  const openFlowDialog = (flow = null) => {
    if (flow) {
      setFlowForm({
        name: flow.name,
        description: flow.description || '',
        trigger_keywords: flow.trigger_keywords || [],
        steps: flow.steps.length > 0 ? flow.steps : [defaultStep()],
        is_active: flow.is_active
      });
    } else {
      setFlowForm({
        name: '',
        description: '',
        trigger_keywords: [],
        steps: [defaultStep()],
        is_active: true
      });
    }
    setFlowDialog({ open: true, data: flow });
  };

  const handleSaveFlow = async () => {
    if (!flowForm.name.trim()) {
      toast.error('Flow name is required');
      return;
    }
    
    // Clean up steps - remove empty questions
    const cleanedSteps = flowForm.steps.filter(step => 
      step.question.trim() || step.solution.trim()
    );
    
    const data = {
      ...flowForm,
      steps: cleanedSteps
    };
    
    try {
      if (flowDialog.data) {
        await updateTroubleshootingFlow(flowDialog.data.id, data);
        toast.success('Flow updated');
      } else {
        await createTroubleshootingFlow(data);
        toast.success('Flow created');
      }
      setFlowDialog({ open: false, data: null });
      loadFlows();
    } catch (error) {
      toast.error('Failed to save flow');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteTroubleshootingFlow(deleteDialog.id);
      toast.success('Flow deleted');
      setDeleteDialog({ open: false, id: null });
      loadFlows();
    } catch (error) {
      toast.error('Failed to delete flow');
    }
  };

  const handleAddKeyword = () => {
    if (keywordInput.trim() && !flowForm.trigger_keywords.includes(keywordInput.trim())) {
      setFlowForm(prev => ({
        ...prev,
        trigger_keywords: [...prev.trigger_keywords, keywordInput.trim()]
      }));
      setKeywordInput('');
    }
  };

  const handleRemoveKeyword = (keyword) => {
    setFlowForm(prev => ({
      ...prev,
      trigger_keywords: prev.trigger_keywords.filter(k => k !== keyword)
    }));
  };

  const handleAddStep = () => {
    setFlowForm(prev => ({
      ...prev,
      steps: [...prev.steps, defaultStep()]
    }));
  };

  const handleRemoveStep = (index) => {
    setFlowForm(prev => ({
      ...prev,
      steps: prev.steps.filter((_, i) => i !== index)
    }));
  };

  const handleStepChange = (index, field, value) => {
    setFlowForm(prev => ({
      ...prev,
      steps: prev.steps.map((step, i) => 
        i === index ? { ...step, [field]: value } : step
      )
    }));
  };

  const toggleFlowExpanded = (flowId) => {
    setExpandedFlows(prev => ({
      ...prev,
      [flowId]: !prev[flowId]
    }));
  };

  return (
    <div className="p-6" data-testid="troubleshooting-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Troubleshooting Flows</h1>
          <p className="text-muted-foreground mt-1">
            Configure diagnostic flows for common issues
          </p>
        </div>
        <Button 
          onClick={() => openFlowDialog()}
          className="bg-primary text-primary-foreground hover:bg-primary/90"
          data-testid="add-flow-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Flow
        </Button>
      </div>

      {/* Flows List */}
      <ScrollArea className="h-[calc(100vh-12rem)]">
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 skeleton rounded-lg" />
            ))}
          </div>
        ) : flows.length > 0 ? (
          <div className="space-y-4">
            {flows.map((flow) => (
              <Collapsible
                key={flow.id}
                open={expandedFlows[flow.id]}
                onOpenChange={() => toggleFlowExpanded(flow.id)}
              >
                <Card className="bg-card/50 border-border/60" data-testid={`flow-${flow.id}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-amber-500/10">
                            <AlertTriangle className="w-5 h-5 text-amber-500" />
                          </div>
                          <div>
                            <CardTitle className="text-lg">{flow.name}</CardTitle>
                            {flow.description && (
                              <p className="text-sm text-muted-foreground mt-1">
                                {flow.description}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 mt-3">
                          <Badge variant={flow.is_active ? "default" : "secondary"} className={cn(
                            flow.is_active ? "bg-emerald-500/10 text-emerald-500" : ""
                          )}>
                            {flow.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {flow.steps.length} steps
                          </span>
                          <span className="text-xs text-muted-foreground">•</span>
                          {flow.trigger_keywords.slice(0, 3).map((kw) => (
                            <Badge key={kw} variant="outline" className="text-xs">
                              {kw}
                            </Badge>
                          ))}
                          {flow.trigger_keywords.length > 3 && (
                            <span className="text-xs text-muted-foreground">
                              +{flow.trigger_keywords.length - 3} more
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => openFlowDialog(flow)}
                          data-testid={`edit-flow-${flow.id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="text-destructive"
                          onClick={() => setDeleteDialog({ open: true, id: flow.id })}
                          data-testid={`delete-flow-${flow.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                        <CollapsibleTrigger asChild>
                          <Button size="icon" variant="ghost">
                            {expandedFlows[flow.id] ? (
                              <ChevronUp className="w-4 h-4" />
                            ) : (
                              <ChevronDown className="w-4 h-4" />
                            )}
                          </Button>
                        </CollapsibleTrigger>
                      </div>
                    </div>
                  </CardHeader>
                  <CollapsibleContent>
                    <CardContent className="pt-4 border-t border-border/50">
                      <div className="space-y-4">
                        {flow.steps.map((step, index) => (
                          <div 
                            key={step.id} 
                            className="flex items-start gap-4 p-3 rounded-lg bg-muted/30"
                          >
                            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                              {step.solution ? (
                                <CheckCircle2 className="w-4 h-4 text-primary" />
                              ) : (
                                <HelpCircle className="w-4 h-4 text-primary" />
                              )}
                            </div>
                            <div className="flex-1">
                              {step.question && (
                                <p className="font-medium">{step.question}</p>
                              )}
                              {step.options && step.options.length > 0 && (
                                <div className="flex flex-wrap gap-2 mt-2">
                                  {step.options.map((opt) => (
                                    <Badge key={opt.text} variant="secondary" className="gap-1">
                                      {opt.text}
                                      <ArrowRight className="w-3 h-3" />
                                    </Badge>
                                  ))}
                                </div>
                              )}
                              {step.solution && (
                                <div className="mt-2 p-2 rounded bg-emerald-500/10 border border-emerald-500/20">
                                  <p className="text-sm text-emerald-400">
                                    <strong>Solution:</strong> {step.solution}
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <AlertTriangle className="w-12 h-12 text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">No troubleshooting flows</p>
            <p className="text-sm text-muted-foreground/70 mt-1">
              Create diagnostic flows to help users resolve issues
            </p>
          </div>
        )}
      </ScrollArea>

      {/* Flow Dialog */}
      <Dialog open={flowDialog.open} onOpenChange={(open) => setFlowDialog({ open, data: null })}>
        <DialogContent className="max-w-3xl max-h-[90vh]" data-testid="flow-dialog">
          <DialogHeader>
            <DialogTitle>
              {flowDialog.data ? 'Edit Flow' : 'New Troubleshooting Flow'}
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh]">
            <div className="space-y-6 py-4 pr-4">
              {/* Basic Info */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Flow Name</label>
                  <Input
                    value={flowForm.name}
                    onChange={(e) => setFlowForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., EA Not Trading"
                    data-testid="flow-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Description</label>
                  <Textarea
                    value={flowForm.description}
                    onChange={(e) => setFlowForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Brief description of this flow"
                    rows={2}
                    data-testid="flow-description-input"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={flowForm.is_active}
                    onCheckedChange={(checked) => setFlowForm(prev => ({ ...prev, is_active: checked }))}
                    data-testid="flow-active-switch"
                  />
                  <label className="text-sm font-medium">Active</label>
                </div>
              </div>

              {/* Trigger Keywords */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Trigger Keywords</label>
                <p className="text-xs text-muted-foreground">
                  Keywords that trigger this flow when user mentions them
                </p>
                <div className="flex gap-2">
                  <Input
                    value={keywordInput}
                    onChange={(e) => setKeywordInput(e.target.value)}
                    placeholder="Add keyword"
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddKeyword())}
                    data-testid="trigger-keyword-input"
                  />
                  <Button type="button" variant="secondary" onClick={handleAddKeyword}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {flowForm.trigger_keywords.map((kw) => (
                    <Badge key={kw} variant="outline" className="gap-1">
                      {kw}
                      <button
                        type="button"
                        onClick={() => handleRemoveKeyword(kw)}
                        className="ml-1 hover:text-destructive"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Steps */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Diagnostic Steps</label>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={handleAddStep}
                    data-testid="add-step-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Step
                  </Button>
                </div>
                
                <div className="space-y-4">
                  {flowForm.steps.map((step, index) => (
                    <Card key={step.id} className="bg-muted/30 border-border/50">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-4">
                          <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 mt-1">
                            <span className="text-sm font-mono text-primary">{index + 1}</span>
                          </div>
                          <div className="flex-1 space-y-3">
                            <div className="space-y-2">
                              <label className="text-xs font-medium text-muted-foreground">Question</label>
                              <Input
                                value={step.question}
                                onChange={(e) => handleStepChange(index, 'question', e.target.value)}
                                placeholder="Ask a diagnostic question..."
                                data-testid={`step-question-${index}`}
                              />
                            </div>
                            <div className="space-y-2">
                              <label className="text-xs font-medium text-muted-foreground">
                                Solution (if this is the final step)
                              </label>
                              <Textarea
                                value={step.solution}
                                onChange={(e) => handleStepChange(index, 'solution', e.target.value)}
                                placeholder="Provide the solution..."
                                rows={2}
                                data-testid={`step-solution-${index}`}
                              />
                            </div>
                          </div>
                          {flowForm.steps.length > 1 && (
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              className="text-destructive"
                              onClick={() => handleRemoveStep(index)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFlowDialog({ open: false, data: null })}>
              Cancel
            </Button>
            <Button onClick={handleSaveFlow} className="bg-primary text-primary-foreground" data-testid="save-flow-btn">
              <Save className="w-4 h-4 mr-2" />
              Save Flow
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, id: null })}>
        <AlertDialogContent data-testid="delete-flow-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Flow</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this troubleshooting flow? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-flow-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
